import type { Program } from '@/types/program';
import { Pill } from '@/ui';
import { cn } from '@/ui/lib/utils';
import snakeToTitleCase from '@/utils/snakeToTitleCase';

import './styles.css';
import type { Props } from './types';

type RowState = 'unchanged' | 'expected' | 'unexpected' | 'new';

interface SummaryRow {
  label: string;
  from: string | null;
  to: string;
  state: RowState;
}

const primaryConcern = (program: Program) => snakeToTitleCase(program.drug_category);
const phaseType = (program: Program) => snakeToTitleCase(program.phase_type_name);
const programFamily = (program: Program) => program.name;

const WARNING_ROW = 'rounded bg-yellow-50 font-semibold dark:bg-yellow-950';
const WARNING_PILL: Partial<Record<RowState, string>> = { unexpected: 'Changing' };

/**
 * Splits a program into the same parts staff pick it by in ProgramSelect, so a
 * phase type change reads differently from an accidental change of concern.
 * Only those accidental-looking changes get color.
 */
const summaryRows = (fromProgram: Program | null, toProgram: Program): SummaryRow[] => {
  const dimensions = [
    { label: 'Primary Concern', value: primaryConcern, changeIsExpected: false },
    { label: 'Phase Type', value: phaseType, changeIsExpected: true },
    { label: 'Program Family', value: programFamily, changeIsExpected: true }
  ];

  return dimensions.map(({ label, value, changeIsExpected }) => {
    const to = value(toProgram);
    if (!fromProgram) return { label, from: null, to, state: 'new' };
    const from = value(fromProgram);
    let state: RowState = 'unchanged';
    if (from !== to) state = changeIsExpected ? 'expected' : 'unexpected';
    return { label, from, to, state };
  });
};

const ProgramChangeSummary = ({ fromProgram, toProgram }: Props) => {
  const rows = summaryRows(fromProgram, toProgram);

  return (
    <dl data-testid="program-change-summary" className="flex flex-col gap-1 text-sm">
      {rows.map((row) => {
        const warningPill = WARNING_PILL[row.state];
        return (
          <div
            key={row.label}
            role="group"
            aria-label={row.label}
            data-state={row.state}
            className={cn(
              'flex items-center gap-3 px-2 py-1',
              warningPill && WARNING_ROW,
              row.state === 'unchanged' && 'text-gray-500 dark:text-gray-400'
            )}>
            <dt className="w-32 shrink-0">{row.label}</dt>
            <dd className="flex flex-1 flex-wrap items-center justify-between gap-2">
              <span>
                {row.from !== null && row.from !== row.to ? `${row.from} → ${row.to}` : row.to}
                {row.state === 'unchanged' && <span className="ml-1">(no change)</span>}
              </span>
              {warningPill && (
                <Pill color="warning" size="sm">
                  {warningPill}
                </Pill>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
};

export default ProgramChangeSummary;
